import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import type { Message } from '../../api/types'
import { Icon } from '../../components/Icon'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { createEmojiIndex, type EmojiEntry, type EmojiGroup } from '../../lib/emojiSearch'
import { TOUCH_SCREEN_QUERY } from '../../lib/platform'
import { loadRecentReactions } from '../../lib/recentReactions'
import { useConversation } from './ConversationContext'

/** The emoji data is a chunk of its own, fetched the first time the picker opens. */
export const emojiDataSource = {
  load: (): Promise<{ groups: EmojiGroup[] }> =>
    import('../../lib/emojiData.json').then((module) => ({ groups: module.default.groups as EmojiGroup[] })),
}

let loading: Promise<EmojiGroup[]> | null = null

function loadEmojiGroups(): Promise<EmojiGroup[]> {
  loading ??= emojiDataSource.load().then(
    (data) => data.groups,
    (error: unknown) => {
      // Try the download again next time.
      loading = null
      throw error
    },
  )
  return loading
}

interface Props {
  /** The message being reacted to; none when the pick goes into the message box. */
  message?: Message
  onPick: (emoji: string) => void
  onBack: () => void
  /** What the back button says: "Back to message actions" in the message sheet. */
  backLabel?: string
  /** 'react' (the message sheet) or 'insert' (the message box): how each emoji is named. */
  use?: 'react' | 'insert'
}

/** Any emoji, as a reaction or into the message box: recent reactions first, then the Unicode groups, with search. */
export function EmojiPicker({ message, onPick, onBack, backLabel = 'Back to message actions', use = 'react' }: Props) {
  const { me } = useConversation()
  // On touch screens focusing the search would cover the picker with the keyboard.
  const touch = useMediaQuery(TOUCH_SCREEN_QUERY)
  const id = useId()
  const [groups, setGroups] = useState<EmojiGroup[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [query, setQuery] = useState('')
  const [recent] = useState(loadRecentReactions)
  const searchRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    loadEmojiGroups().then(
      (loaded) => !cancelled && setGroups(loaded),
      () => !cancelled && setFailed(true),
    )
    return () => {
      cancelled = true
    }
  }, [attempt])

  const index = useMemo(() => (groups ? createEmojiIndex(groups) : null), [groups])
  const byEmoji = useMemo(() => new Map((groups ?? []).flatMap((g) => g.emoji.map((entry) => [entry[0], entry] as const))), [groups])
  const reactions = message?.reactions
  const mine = useMemo(() => new Set((reactions ?? []).filter((r) => r.userIds.includes(me.id)).map((r) => r.emoji)), [reactions, me.id])
  const searching = query.trim() !== ''
  const results = useMemo(() => (index && searching ? index.search(query) : []), [index, searching, query])

  const sections: { name: string; emoji: EmojiEntry[] }[] = !groups
    ? []
    : searching
      ? [{ name: 'Search results', emoji: results }]
      : [
          ...(recent.length ? [{ name: 'Recently used', emoji: recent.map((e): EmojiEntry => byEmoji.get(e) ?? [e, e, '']) }] : []),
          ...groups,
        ]
  const sectionId = (name: string) => `${id}-${name.replace(/\W+/g, '-')}`

  const onGridKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = [...event.currentTarget.querySelectorAll<HTMLElement>('.emoji-grid button')]
    const current = buttons.indexOf(document.activeElement as HTMLElement)
    if (current < 0) return
    const grid = buttons[current].parentElement
    const columns = grid ? getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1 : 1
    const step = ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns } as Record<string, number>)[event.key]
    if (!step) return
    event.preventDefault()
    const next = buttons[current + step]
    if (next) next.focus()
    else if (step < 0) searchRef.current?.focus()
  }

  return (
    <div className="emoji-picker">
      <div className="emoji-picker-head">
        <button type="button" className="icon-button" aria-label={backLabel} data-autofocus={touch || undefined} onClick={onBack}>
          <Icon name="back" />
        </button>
        <div className="search-field">
          <Icon name="search" />
          <input
            ref={searchRef}
            type="search"
            aria-label="Search emoji"
            placeholder="Search emoji"
            autoComplete="off"
            data-autofocus={!touch || undefined}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) {
                e.preventDefault()
                onPick(results[0][0])
              } else if (e.key === 'ArrowDown') {
                e.preventDefault()
                scrollRef.current?.querySelector<HTMLElement>('.emoji-grid button')?.focus()
              }
            }}
          />
        </div>
      </div>

      {groups && !searching ? (
        <div className="emoji-group-bar" role="group" aria-label="Go to a group">
          {groups.map((group) => (
            <button
              key={group.name}
              type="button"
              aria-label={group.name}
              title={group.name}
              onClick={() => {
                const section = document.getElementById(sectionId(group.name))
                if (section && scrollRef.current) scrollRef.current.scrollTop = section.offsetTop
              }}
            >
              {group.icon}
            </button>
          ))}
        </div>
      ) : null}

      <div
        className="emoji-scroll"
        ref={scrollRef}
        onKeyDown={onGridKeys}
        onFocus={(event) => {
          // One emoji is in the Tab order: the one focused last, so Tab and Shift+Tab come back to it.
          const target = event.target as HTMLElement
          if (!target.matches('.emoji-grid button')) return
          event.currentTarget.querySelectorAll<HTMLElement>('.emoji-grid button[tabindex="0"]').forEach((b) => (b.tabIndex = -1))
          target.tabIndex = 0
        }}
      >
        {failed ? (
          <p className="picker-status">
            Couldn't load emoji.{' '}
            <button
              type="button"
              className="text-button"
              onClick={() => {
                setFailed(false)
                setAttempt((n) => n + 1)
              }}
            >
              Try again
            </button>
          </p>
        ) : !groups ? (
          <p className="picker-status">
            <span className="spinner" aria-hidden="true" /> Loading emoji…
          </p>
        ) : searching && results.length === 0 ? (
          <p className="picker-status">No emoji found</p>
        ) : (
          sections.map((section, sectionIndex) => (
            <div key={section.name} className="emoji-section" id={sectionId(section.name)}>
              <h3 id={`${sectionId(section.name)}-heading`}>{section.name}</h3>
              <div className="emoji-grid" role="group" aria-labelledby={`${sectionId(section.name)}-heading`}>
                {section.emoji.map(([emoji, name], i) => {
                  const pressed = mine.has(emoji)
                  return (
                    <button
                      key={emoji}
                      type="button"
                      // The first emoji starts in the Tab order; the arrow keys move between them.
                      tabIndex={sectionIndex === 0 && i === 0 ? 0 : -1}
                      aria-pressed={pressed}
                      aria-label={use === 'insert' ? `Insert ${name}` : `${pressed ? 'Remove your' : 'React with'} ${name}`}
                      title={name}
                      onClick={() => onPick(emoji)}
                    >
                      {emoji}
                    </button>
                  )
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
